import dayjs from 'dayjs'
import { useContext, createContext, useEffect, useState } from 'react'

import { sendMessageImage } from '@/actions/send-message-chat-image'
import { notificationDanger } from '@/components/ui/Notification/Notification'
import { uniqBy } from '@/util/lodash-replacer'
import {
  PersonalEventDocument,
  useGetConversationQuery,
  useGetConversationsQuery,
  useMarkMessageAndOlderAsReadMutation,
  useSendMessageInConversationMutation,
} from '@/views/BuyerSellerChat/operations.generated'

import { useUser } from '../contexts/user/User.context'

import { useUserEvent } from './UserEvent.provider'

import type { Conversation } from '@/network/graphql/types.generated'
import type {
  GetConversationQuery,
  MessageImageFragment,
  MessageTextFragment,
  PersonalEventSubscription,
  PersonalEventSubscriptionVariables,
} from '@/views/BuyerSellerChat/operations.generated'
import type { ReactNode } from 'react'

type ConversationsContext = {
  conversations: (Pick<Conversation, '__typename' | 'id' | 'hasUnreadMessages'> & {
    lastMessage:
      | { __typename?: 'ConversationMessageImage'; id: string; createdAt: Date }
      | { __typename?: 'ConversationMessageText'; id: string; message: string; createdAt: Date }
    otherParticipant: {
      __typename?: 'User'
      id: string
      username: string
      avatarWebPUrl?: string | null
    }
  })[]
  hasMore: boolean
  loading: boolean
  loadConversations: () => void
  refetchConversations: () => void
}

type ConversationsProviderProps = {
  children: ReactNode
}

const ConversationsContext = createContext<ConversationsContext>({
  conversations: [],
  hasMore: false,
  loading: false,
  refetchConversations: () => {},
  loadConversations: () => {},
})

export const ConversationsProvider = ({ children }: ConversationsProviderProps) => {
  const { isConnected } = useUser()
  const { data, loading, fetchMore, refetch } = useGetConversationsQuery({
    variables: {
      first: 20,
    },
    skip: !isConnected,
  })

  const loadConversations = () => {
    fetchMore({
      variables: {
        after: data?.viewer?.conversations?.pageInfo.endCursor,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult.viewer || !fetchMoreResult.viewer.conversations) return prev
        if (!prev || !prev.viewer || !prev.viewer.conversations) return fetchMoreResult
        return {
          viewer: {
            ...prev.viewer,
            conversations: {
              ...prev.viewer.conversations,
              edges: [...prev.viewer.conversations.edges, ...fetchMoreResult.viewer.conversations.edges],
              pageInfo: fetchMoreResult.viewer.conversations.pageInfo,
            },
          },
        }
      },
    })
  }

  const value = {
    conversations: data?.viewer?.conversations?.edges?.map((edge) => edge.node) || [],
    hasMore: data?.viewer?.conversations?.pageInfo?.hasNextPage || false,
    loading,
    refetchConversations: refetch,
    loadConversations,
  }

  return <ConversationsContext.Provider value={value}>{children}</ConversationsContext.Provider>
}

export const useConversations = () => useContext(ConversationsContext)

export const useConversation = (conversationId: string) => {
  const [markAsRead] = useMarkMessageAndOlderAsReadMutation()
  const { refetchNotifications } = useUserEvent()
  const [scrollKey, setScrollKey] = useState(0)

  const { data, fetchMore, loading, subscribeToMore, refetch } = useGetConversationQuery({
    variables: {
      first: 20,
      conversationId: `Conversation|${conversationId}`,
    },
    onCompleted: (data) => {
      if (data.node.__typename === 'Conversation') {
        const lastMessage = data.node.messages.edges.reduce((acc, edge) => {
          if (!acc) {
            return edge.node
          }
          return dayjs(edge.node.createdAt).isAfter(dayjs(acc.createdAt)) ? edge.node : acc
        }, data.node.messages.edges[0]?.node)

        markAsRead({
          variables: {
            input: {
              messageId: lastMessage.id,
            },
          },
          update: (cache, { data }) => {
            const isOk = data?.markMessageAndOlderAsRead.ok
            if (!isOk) {
              return
            }

            const conversationRef = cache.identify({
              __typename: 'Conversation',
              id: `Conversation|${conversationId}`,
            })

            cache.modify({
              id: conversationRef,
              fields: {
                hasUnreadMessages: () => false,
              },
            })
          },
          onCompleted: () => {
            refetchNotifications()
          },
        })
      }
    },
    skip: !conversationId,
  })

  useEffect(() => {
    subscribeToMore<PersonalEventSubscription, PersonalEventSubscriptionVariables>({
      document: PersonalEventDocument,
      updateQuery: updateConversationWithSubscription,
    })
  }, [conversationId])

  function updateConversationWithSubscription(
    prev: GetConversationQuery,
    {
      subscriptionData,
    }: {
      subscriptionData: {
        data: PersonalEventSubscription
      }
      variables?: PersonalEventSubscriptionVariables
    }
  ): GetConversationQuery {
    if (!subscriptionData.data) return prev

    const data =
      subscriptionData.data?.personalEvent.__typename === 'PersonalEventConversationNewMessage'
        ? subscriptionData.data.personalEvent
        : undefined

    const prevNode = prev?.node?.__typename === 'Conversation' ? prev.node : null

    if (!prevNode || !data) {
      return prev
    }

    if (prevNode.id !== data.message.conversationId) {
      return prev
    }
    const edges = prevNode.messages.edges.concat({
      node: data.message,
      __typename: 'ConversationMessageEdge',
    })
    setScrollKey((prev) => prev + 1)

    return {
      __typename: 'Query',
      node: {
        __typename: 'Conversation',
        id: prevNode.id,
        otherParticipant: prevNode.otherParticipant,
        messages: {
          pageInfo: prevNode.messages.pageInfo,
          __typename: 'ConversationMessageConnection',
          edges,
        },
      },
    }
  }

  const conversation = data?.node.__typename === 'Conversation' ? data.node : undefined
  const pageInfo = conversation?.messages.pageInfo
  const hasMore = conversation?.messages.pageInfo?.hasNextPage || false

  const [sendMessageInConversation] = useSendMessageInConversationMutation({
    update: (cache, { data }) => {
      if (!data?.sendMessageInConversation?.message) return

      const newMessage = data.sendMessageInConversation
      const conversationRef = cache.identify({
        __typename: 'Conversation',
        id: `Conversation|${conversationId}`,
      })

      cache.modify({
        id: conversationRef,
        fields: {
          messages: (existing) => {
            const newEdge = {
              __typename: newMessage.__typename,
              node: {
                __ref: 'ConversationMessageText:' + newMessage.id,
              },
            }

            return {
              ...existing,
              edges: [...existing.edges, newEdge],
            }
          },
        },
      })
    },
  })

  const loadMore = () => {
    fetchMore({
      variables: {
        after: pageInfo?.endCursor,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult.node || fetchMoreResult.node.__typename !== 'Conversation') return prev
        if (!prev || !prev.node || prev.node.__typename !== 'Conversation') return fetchMoreResult
        return {
          node: {
            ...prev.node,
            messages: {
              ...prev.node.messages,
              edges: [...prev.node.messages.edges, ...fetchMoreResult.node.messages.edges],
              pageInfo: fetchMoreResult.node.messages.pageInfo,
            },
          },
        }
      },
    })
  }

  const sendMessage = (content: string) => {
    if (!conversationId) return Promise.reject()
    return sendMessageInConversation({
      variables: {
        input: {
          content,
          conversationId: `Conversation|${conversationId}`,
        },
      },
      onError: (error) => {
        notificationDanger(error.message)
      },
    })
  }

  const sendImageMessage = async (image: File, conversationId: string) => {
    await sendMessageImage({ image, conversationId })
    refetch()
  }

  function formatMessages(messages: (MessageTextFragment | MessageImageFragment)[]) {
    return uniqBy(messages, (message) => message.id).sort((a, b) =>
      dayjs(a.createdAt).isBefore(dayjs(b.createdAt)) ? -1 : 1
    )
  }

  return {
    otherParticipant: data?.node.__typename === 'Conversation' ? data.node.otherParticipant : undefined,
    messages:
      data?.node.__typename === 'Conversation'
        ? formatMessages((data.node.messages.edges || []).map((x) => x.node))
        : [],
    hasMore,
    loading,
    scrollKey,
    sendMessage,
    sendImageMessage,
    loadMore,
  }
}
